/**
 * useSemanticSearch · hook con debounce de 350ms sobre la edge function
 * core-semantic-search. Devuelve resultados unificados (prospects + tasks).
 *
 * NO se dispara hasta que el query tiene >= 2 chars. Cancela request si
 * el user sigue escribiendo (TanStack Query lo maneja automático por la
 * queryKey que cambia con el query).
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { supabase } from '@/lib/supabase';

export interface SemanticHit {
  entity_type: 'prospect' | 'task';
  id: string;
  title: string;
  subtitle: string;
  url: string;
  score: number;
}

function useDebounced<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function useSemanticSearch(query: string, limit = 5) {
  const debouncedQuery = useDebounced(query.trim(), 350);
  const enabled = debouncedQuery.length >= 2;

  return useQuery({
    queryKey: ['semantic-search', debouncedQuery, limit],
    enabled,
    staleTime: 60_000, // mismo query no re-pide en 1 min
    queryFn: async (): Promise<SemanticHit[]> => {
      const { data, error } = await supabase.functions.invoke(
        'core-semantic-search',
        { body: { query: debouncedQuery, limit } },
      );
      if (error) throw error;
      return (data?.results ?? []) as SemanticHit[];
    },
  });
}
